import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Music, Settings } from "lucide-react";
import { BPMSyncIndicator } from "./BPMSyncIndicator";
import { UnifiedMusicPlayer } from "./UnifiedMusicPlayer";
import { ProviderSelector } from "./ProviderSelector";
import { BPMSensitivityControl } from "./BPMSensitivityControl";
import { useAdaptiveMusic } from "@/hooks/useAdaptiveMusic";
import { useUserAge } from "@/hooks/useUserAge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface AdaptiveMusicPlayerProps {
  currentBPM: number;
  zone: string | null;
  isRunning: boolean;
}

export const AdaptiveMusicPlayer = ({ 
  currentBPM, 
  zone, 
  isRunning
}: AdaptiveMusicPlayerProps) => {
  const userAge = useUserAge();
  const [bpmThreshold, setBpmThreshold] = useState(15);
  const [showSettings, setShowSettings] = useState(false);

  // Load saved threshold from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('music_bpm_threshold');
    if (saved) {
      setBpmThreshold(parseInt(saved, 10));
    }
  }, []);

  // Save threshold to localStorage
  const handleThresholdChange = (value: number) => {
    setBpmThreshold(value);
    localStorage.setItem('music_bpm_threshold', value.toString());
  };

  const adaptiveMusic = useAdaptiveMusic({
    isRunning,
    userAge,
    autoSync: true,
    bpmThreshold
  });

  if (!isRunning) {
    return (
      <div className="space-y-4">
        <ProviderSelector
          selectedProvider={adaptiveMusic.providerType}
          isAuthenticated={adaptiveMusic.isAuthenticated}
          isConnecting={false}
          onProviderChange={adaptiveMusic.switchProvider}
          onConnect={adaptiveMusic.connect}
          onDisconnect={adaptiveMusic.disconnect}
        />
        <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <Music className="w-12 h-12 text-muted-foreground" />
            <div>
              <h3 className="font-semibold mb-1">Música Adaptativa</h3>
              <p className="text-sm text-muted-foreground">
                Conecte-se a um serviço de música e inicie sua corrida para ativar a sincronização automática
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ProviderSelector
        selectedProvider={adaptiveMusic.providerType}
        isAuthenticated={adaptiveMusic.isAuthenticated}
        isConnecting={false}
        onProviderChange={adaptiveMusic.switchProvider}
        onConnect={adaptiveMusic.connect}
        onDisconnect={adaptiveMusic.disconnect}
      />

      <Card className="p-6 space-y-4 bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Music className="w-5 h-5 text-primary" />
            <h3 className="font-semibold">Música Adaptativa</h3>
            {adaptiveMusic.isReady && (
              <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded-full">
                {adaptiveMusic.providerName}
              </span>
            )}
          </div>
          <Dialog open={showSettings} onOpenChange={setShowSettings}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Settings className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Configurações de Música Adaptativa</DialogTitle>
                <DialogDescription>
                  Ajuste como a música responde às mudanças no seu BPM
                </DialogDescription>
              </DialogHeader>
              <BPMSensitivityControl
                threshold={bpmThreshold}
                onThresholdChange={handleThresholdChange}
              />
            </DialogContent>
          </Dialog>
        </div>

        {adaptiveMusic.currentTrack && (
          <>
            <BPMSyncIndicator
              currentBPM={adaptiveMusic.currentBPM}
              trackBPM={adaptiveMusic.currentTrack.bpm || 0}
              isSynced={adaptiveMusic.isSynced}
              isAdjusting={adaptiveMusic.isAdjusting}
              isCrossfading={adaptiveMusic.isCrossfading}
            />
            <BPMSensitivityControl
              threshold={bpmThreshold}
              onThresholdChange={handleThresholdChange}
              compact
            />
          </>
        )}

        {adaptiveMusic.isAuthenticated ? (
          adaptiveMusic.currentTrack ? (
            <UnifiedMusicPlayer
              currentTrack={adaptiveMusic.currentTrack}
              isPlaying={adaptiveMusic.isPlaying}
              position={adaptiveMusic.playerState?.position || 0}
              duration={adaptiveMusic.playerState?.duration || 0}
              volume={adaptiveMusic.playerState?.volume || 100}
              providerName={adaptiveMusic.providerName}
              onPlayPause={async () => {
                if (adaptiveMusic.isPlaying) {
                  await adaptiveMusic.pause();
                } else {
                  await adaptiveMusic.resume();
                }
              }}
              onNext={adaptiveMusic.next}
              onPrevious={adaptiveMusic.previous}
              onSeek={adaptiveMusic.seek}
              onVolumeChange={adaptiveMusic.setVolume}
            />
          ) : (
            <div className="text-center py-8">
              <Button
                onClick={adaptiveMusic.startMusic}
                disabled={adaptiveMusic.currentBPM === 0 || !adaptiveMusic.isAuthenticated}
                className="gap-2"
              >
                <Music className="w-4 h-4" />
                Iniciar Música Adaptativa
              </Button>
              {adaptiveMusic.currentBPM === 0 ? (
                <p className="text-xs text-muted-foreground mt-2">
                  Aguardando dados de BPM...
                </p>
              ) : !adaptiveMusic.isAuthenticated ? (
                <p className="text-xs text-muted-foreground mt-2">
                  Conecte-se a um serviço de música acima
                </p>
              ) : null}
            </div>
          )
        ) : (
          <div className="text-center py-8 space-y-2">
            <p className="text-sm text-muted-foreground">
              Conecte-se a um serviço de música para começar
            </p>
          </div>
        )}

        {adaptiveMusic.playlist.length > 1 && (
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-muted-foreground">Próximas músicas</h4>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {adaptiveMusic.playlist.slice(1, 4).map((track, index) => (
                <div
                  key={index}
                  className="text-xs p-2 rounded bg-background/30 flex justify-between items-center"
                >
                  <span className="truncate flex-1">
                    {track.name} - {track.artist}
                  </span>
                  {track.bpm && (
                    <span className="text-muted-foreground ml-2">{track.bpm} BPM</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
